import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { magicQuoteApi } from '../api/endpoints.ts';
import { ApiRequestError } from '../api/client.ts';
import { useSession } from '../state/session.tsx';
import { startLocalWalkthrough } from '../state/local-walkthrough.ts';
import { newLocalWalkthroughId } from '../state/walkthrough-id.ts';
import { useOnlineStatus } from './index.ts';
import { useAiDisclosure } from '../components/ai-disclosure.tsx';

export interface StartWalkthroughContext {
  customerId: string | null;
  /**
   * The chosen customer's name, as it was shown when they were chosen.
   *
   * Copied in rather than looked up later, because looking it up is a network
   * read: a walkthrough started offline for a customer already on file would
   * otherwise be indistinguishable from one started for nobody.
   */
  customerName: string | null;
  propertyId: string | null;
  /** The property's nickname or street line — a label to recognize a row by, not an address to map. */
  propertyLabel: string | null;
}

/**
 * Starts a Magic Quote walkthrough, on the server when there is one to reach
 * and on this device when there is not.
 *
 * One hook rather than two copies of the mutation, because there are two
 * places a walk can start from — the "New quote" page and the Walkthroughs
 * tab's empty state — and the offline story below is exactly the kind of
 * thing that drifts when it is written twice.
 */
export function useStartWalkthrough(context: StartWalkthroughContext) {
  const { t } = useTranslation();
  const disclosure = useAiDisclosure();
  const { company, currentLocation } = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const online = useOnlineStatus();

  const scope = { companyId: company?.id ?? null, locationId: currentLocation?.id ?? null };

  /**
   * Starts one here, on this device, and remembers enough to show it.
   */
  const startHere = async (localId: string) => {
    const record = await startLocalWalkthrough({
      localId,
      companyId: scope.companyId,
      // The branch this walk belongs to. Without it the guard in adoption has
      // nothing to compare and every walkthrough is handed over into whichever
      // branch the estimator happens to be in when the signal returns.
      locationId: scope.locationId,
      customerId: context.customerId,
      customerName: context.customerName,
      propertyId: context.propertyId,
      propertyAddress: context.propertyLabel,
      mode: 'free',
    });
    // No local store means no record, and a walkthrough whose id nothing
    // remembers is one nobody can find again — better refused here, in a
    // driveway, than discovered at the end of a walk.
    if (!record) throw new Error(t('magicQuote.home.offlineStartFailed'));
    return { id: record.localId };
  };

  const start = useMutation({
    /**
     * The mutation must be allowed to run when there is no network, because
     * running is how it starts a walkthrough without one.
     *
     * react-query's default `networkMode: 'online'` PAUSES a mutation whose
     * browser has reported itself offline — `mutationFn` never executes, so
     * the offline branch inside it never runs, and the Start button spins
     * until the connection returns.
     */
    networkMode: 'always',
    mutationFn: async () => {
      // Minted before anything is asked of the network, and sent with the
      // request. This is the case the id exists for: the POST reaches the
      // server, the reply is lost, and the phone has no way to tell that from
      // the request never arriving. Both look like a reason to start the walk
      // locally — and with the id already agreed, the walkthrough the server
      // made is the walkthrough this device is holding. Without it, the
      // fallback would mint a second id and the company would end up with two
      // walkthroughs of one house, one of them empty.
      const localId = newLocalWalkthroughId();

      // Always the free walkthrough — no prompts, the cleaner just walks and
      // records. The other two modes exist in the data model and are not
      // offered here, because `quick` behaves identically and `guided` has no
      // prompts to give yet: a picker whose options do the same thing is a
      // promise the software does not keep.
      if (!online) return startHere(localId);
      try {
        return await magicQuoteApi.start({
          customerId: context.customerId,
          propertyId: context.propertyId,
          mode: 'free',
          clientSessionId: localId,
        });
      } catch (cause) {
        // `navigator.onLine` is a claim about the network adapter, not about
        // whether anything is reachable — one bar in a driveway reports online
        // and answers nothing. A transport failure (no `ApiRequestError`, so
        // no response ever came back) is the same situation arriving a second
        // later, and it gets the same answer: start the walk anyway.
        if (cause instanceof ApiRequestError) throw cause;
        return startHere(localId);
      }
    },
    onSuccess: (session) => {
      // The Walkthroughs tab's list was fetched before the walkthrough
      // existed, and it is 30 seconds from being stale — long enough that a
      // cleaner who starts a walk and immediately presses Back is shown a
      // screen with no sign of it.
      void queryClient.invalidateQueries({ queryKey: ['magic-quotes'] });
      navigate(`/magic-quote/${session.id}`);
    },
  });

  return {
    start: () => {
      // Offline, the walk never leaves the phone. Waiting on the disclosure
      // request would refuse that start: the fetch cannot succeed, and the
      // rejection used to be swallowed before the local branch ran. The notice
      // is asked when a later sync actually sends something.
      if (!online) {
        start.mutate();
        return;
      }
      void disclosure
        .ensure(true)
        .then(() => start.mutate())
        .catch(() => undefined);
    },
    isPending: start.isPending,
    error: start.error as Error | null,
    online,
    dialog: disclosure.dialog,
  };
}
